// The four weekly tables the research said beat any model until there
// are a couple of hundred closed deals: response by touch number,
// connect rate by hour and weekday, progress by store attribute, and
// which touch produced the first response on the stores that moved.
// Pure: computed from the touch log and the prospect rows.
import { CADENCE, areaOf } from './salesPlan';
import { SKIPPED_OUTCOMES } from './salesGame';

// Outcomes that mean a human on the other end engaged.
export const POSITIVE = new Set(['spoke', 'booked', 'replied', 'reply_received', 'showed', 'pilot', 'not_now', 'converted', 'referrals', 'restart', 'thinking']);
const CONNECT = new Set(['spoke', 'booked', 'not_now', 'pilot', 'bad_number']);
const REAL = (t) => t.kind !== 'note' && t.kind !== 'stage' && t.kind !== 'assign';
const pct = (n, d) => (d ? Math.round((n / d) * 100) : null);

export const MIN_TOUCHES = 20;

/** Response by touch number: for each step of the sequence, touches made and the share that got a response. */
export function responseByStep(touches) {
  const rows = CADENCE.map((s, i) => ({ step: i + 1, key: s.key, label: s.short, kind: s.kind, touches: 0, positive: 0, moved: 0 }));
  for (const t of touches) {
    if (!REAL(t) || t.step == null || !(t.from_stage === 'new' || t.from_stage === 'working')) continue;
    const r = rows[t.step];
    if (!r) continue;
    r.touches += 1;
    if (POSITIVE.has(t.outcome)) r.positive += 1;
    if (t.to_stage && t.to_stage !== t.from_stage && (t.to_stage === 'demo' || t.to_stage === 'pilot')) r.moved += 1;
  }
  return rows.map((r) => ({ ...r, positivePct: pct(r.positive, r.touches), movedPct: pct(r.moved, r.touches) }));
}

/** Calls by local hour: dials, and the share where someone picked up. */
export function connectByHour(touches) {
  const h = Array.from({ length: 24 }, (_, i) => ({ hour: i, calls: 0, connects: 0 }));
  for (const t of touches) {
    if (t.kind !== 'call' || SKIPPED_OUTCOMES.has(t.outcome)) continue;
    const d = new Date(t.happened_at);
    if (isNaN(d)) continue;
    const r = h[d.getHours()];
    r.calls += 1;
    if (CONNECT.has(t.outcome)) r.connects += 1;
  }
  return h.map((r) => ({ ...r, pct: pct(r.connects, r.calls) })).filter((r) => r.hour >= 7 && r.hour <= 20);
}

export function connectByWeekday(touches) {
  const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const d7 = names.map((label, i) => ({ day: i, label, calls: 0, connects: 0 }));
  for (const t of touches) {
    if (t.kind !== 'call' || SKIPPED_OUTCOMES.has(t.outcome)) continue;
    const d = new Date(t.happened_at);
    if (isNaN(d)) continue;
    const r = d7[d.getDay()];
    r.calls += 1;
    if (CONNECT.has(t.outcome)) r.connects += 1;
  }
  return d7.map((r) => ({ ...r, pct: pct(r.connects, r.calls) }));
}

/** Tier from the import notes ("A: independent", "B: chain") or the area tag. */
const attrOf = (p) => {
  const n = String(p.notes || '');
  if (/^A:/i.test(n)) return 'Independent';
  if (/^B:/i.test(n)) return 'Chain';
  return 'Other';
};
const PROGRESSED = new Set(['demo', 'pilot', 'customer']);

/** Progress by store attribute: how many stores of each kind moved to Demo or beyond, and how many closed. */
export function progressByAttribute(prospects) {
  const groups = new Map();
  const add = (key, p) => {
    let g = groups.get(key);
    if (!g) { g = { key, stores: 0, contacted: 0, progressed: 0, won: 0, lost: 0 }; groups.set(key, g); }
    g.stores += 1;
    if (p.stage !== 'new' || (p.touch_count || 0) > 0) g.contacted += 1;
    if (PROGRESSED.has(p.stage) || p.demo_at || p.pilot_at || p.won_at) g.progressed += 1;
    if (p.stage === 'customer' || p.won_at) g.won += 1;
    if (p.stage === 'lost') g.lost += 1;
  };
  for (const p of prospects) {
    add(`Tier: ${attrOf(p)}`, p);
    add(p.website ? 'Has a website' : 'No website', p);
    add(p.social ? 'Has a social page' : 'No social page', p);
    if (areaOf(p)) add(`Area: ${areaOf(p)}`, p);
  }
  return [...groups.values()].map((g) => ({ ...g, progressedPct: pct(g.progressed, g.contacted), wonPct: pct(g.won, g.contacted) })).sort((a, b) => a.key.localeCompare(b.key));
}

/** On the stores that moved to Demo or beyond, which touch got the first response. */
export function firstResponseTouch(touches, prospects) {
  const moved = new Set(prospects.filter((p) => PROGRESSED.has(p.stage) || p.demo_at || p.pilot_at || p.won_at).map((p) => p.id));
  const first = new Map();
  const sorted = [...touches].filter((t) => REAL(t) && moved.has(t.prospect_id) && POSITIVE.has(t.outcome)).sort((a, b) => new Date(a.happened_at) - new Date(b.happened_at));
  for (const t of sorted) if (!first.has(t.prospect_id)) first.set(t.prospect_id, t);
  const rows = CADENCE.map((s, i) => ({ step: i + 1, label: s.short, kind: s.kind, stores: 0 }));
  let later = 0;
  for (const t of first.values()) {
    if (t.step != null && rows[t.step] && (t.from_stage === 'new' || t.from_stage === 'working')) rows[t.step].stores += 1;
    else later += 1;
  }
  return { rows, later, total: first.size, movedTotal: moved.size };
}

export function insights(touches, prospects) {
  const real = touches.filter(REAL);
  return {
    enough: real.length >= MIN_TOUCHES,
    touches: real.length,
    byStep: responseByStep(touches),
    byHour: connectByHour(touches),
    byWeekday: connectByWeekday(touches),
    byAttribute: progressByAttribute(prospects),
    firstResponse: firstResponseTouch(touches, prospects),
  };
}
